import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useSite } from "@/context/SiteContext";
import { fileUrl } from "@/lib/api";
import { brandVars } from "@/lib/color";
import { Waves, LogOut, ShieldAlert } from "lucide-react";

export default function DashboardShell({ title, nav, active, onSelect, children, brand }) {
  const { user, logout, impersonating, stopImpersonate } = useAuth();
  const { site } = useSite();
  const navigate = useNavigate();
  const name = brand?.name || site?.site_name || "LaundryDiary";
  const resolveImg = (u) => (u && (u.startsWith("http") || u.startsWith("data:")) ? u : (u ? fileUrl(u) : null));
  const logo = brand?.logo_url ? resolveImg(brand.logo_url) : site?.logo_url;
  const brandStyle = brand?.accent_color ? brandVars(brand.accent_color) : undefined;

  const exitImpersonation = async () => {
    await stopImpersonate();
    navigate("/admin");
  };

  return (
    <div className="min-h-screen bg-slate-50" style={brandStyle} data-testid="dashboard-shell">
      {impersonating && (
        <div className="flex items-center justify-center gap-3 bg-amber-500 px-4 py-2 text-sm font-medium text-white" data-testid="impersonation-banner">
          <ShieldAlert className="h-4 w-4" />
          You're signed in as <b>{user?.name}</b> ({user?.email}).
          <button onClick={exitImpersonation} data-testid="exit-impersonation-btn" className="rounded-md bg-white/20 px-3 py-1 font-semibold hover:bg-white/30">Return to admin</button>
        </div>
      )}
      <div className="flex">
        {/* Sidebar */}
        <aside className="sticky top-0 hidden h-screen w-64 shrink-0 flex-col border-r border-slate-200 bg-white lg:flex">
          <Link to="/" className="flex h-16 items-center gap-2 border-b border-slate-200 px-6">
            {logo ? (
              <img src={logo} alt={name} className="h-7 w-auto max-w-[150px] object-contain" data-testid="shell-logo" />
            ) : (
              <>
                <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-brand text-white">
                  <Waves className="h-4 w-4" strokeWidth={1.5} />
                </span>
                <span className="font-heading text-lg font-bold text-slate-900">{name}</span>
              </>
            )}
          </Link>
          <nav className="flex-1 space-y-1 p-4">
            <p className="px-3 pb-2 text-xs font-bold uppercase tracking-[0.2em] text-slate-400">{title}</p>
            {nav.map((n) => (
              <button
                key={n.key}
                onClick={() => onSelect(n.key)}
                data-testid={`nav-${n.key}`}
                className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all ${
                  active === n.key ? "bg-accent text-brand" : "text-slate-600 hover:bg-slate-50"
                }`}
              >
                <n.icon className="h-4.5 w-4.5" strokeWidth={1.5} />
                {n.label}
              </button>
            ))}
          </nav>
          <div className="border-t border-slate-200 p-4">
            <div className="mb-3 px-3">
              <p className="truncate text-sm font-medium text-slate-900">{user?.name}</p>
              <p className="truncate text-xs text-slate-400">{user?.email}</p>
            </div>
            <button
              onClick={() => { logout(); navigate("/"); }}
              data-testid="dashboard-logout"
              className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-slate-600 hover:bg-slate-50"
            >
              <LogOut className="h-4 w-4" /> Log out
            </button>
          </div>
        </aside>

        {/* Main */}
        <main className="min-w-0 flex-1">
          {/* mobile top bar */}
          <div className="flex items-center justify-between border-b border-slate-200 bg-white px-5 py-3 lg:hidden">
            <Link to="/" className="font-heading text-lg font-bold">{name}</Link>
            <button onClick={() => { logout(); navigate("/"); }} className="text-sm text-slate-500"><LogOut className="h-4 w-4" /></button>
          </div>
          {/* mobile nav */}
          <div className="flex gap-2 overflow-x-auto border-b border-slate-200 bg-white px-4 py-2 lg:hidden">
            {nav.map((n) => (
              <button key={n.key} onClick={() => onSelect(n.key)}
                className={`whitespace-nowrap rounded-lg px-3 py-1.5 text-sm font-medium ${active === n.key ? "bg-accent text-brand" : "text-slate-600"}`}>
                {n.label}
              </button>
            ))}
          </div>
          <div className="p-5 lg:p-8">{children}</div>
        </main>
      </div>
    </div>
  );
}
